import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { filter, map, Observable, of } from 'rxjs';
import { environment } from '@environments/environment';
import {
  ModalMessageComponent,
  PlModalMessageData,
} from '../components/modal-message/modal-message.component';

export enum PlModalType {
  BULK_LEAVE = 'bulk_leave',
  BULK_EMPTY_DATA = 'bulk_empty_dat',
  BULK_ERRORS = 'bulk_errors',
  BULK_USERS_ERRORS = 'bulk_users_errors',
  BULK_IMPORT_AS_IS = 'bulk_import_as_is',
  BULK_SERVER_ERROR = 'bulk_server_error',
  INPUT_WRONG_FILE_TYPE = 'input_wrong_file_type',
  DELETE_OBJECTIVE = 'delete_objective',
  DELETE_GOAL = 'delete_goal',
  CONTACT_SUPPORT = 'contact_support',
  EDIT_OBJECTIVE = 'edit_objective',
}

const supportEmail = `<a target="_blank" href="mailto:support@presence.com">support@presence.com</a>`;

const configurations: { [key: string]: PlModalMessageData } = {
  [PlModalType.BULK_LEAVE]: {
    title: 'You’re about to exit',
    message: 'If you leave now you will lose any unsaved work',
    image: '/assets/img/bulk-upload/leaving.png',
    buttons: [
      {
        value: 'stay',
        type: 'secondary',
        text: 'I’ll finish my import',
      },
      {
        value: 'exit',
        text: 'I’m ready to exit',
      },
    ],
  },
  [PlModalType.BULK_ERRORS]: {
    title: 'We had some errors',
    message: 'We were not able to upload your records',
    image: '/assets/img/bulk-upload/warning.png',
    buttons: [{ text: 'OK' }],
  },
  [PlModalType.BULK_USERS_ERRORS]: {
    title: 'Uh oh.',
    message:
      "We found some errors.<br/> Let's review so we can correctly assign your providers",
    image: '/assets/img/bulk-upload/import-as-is.png',
    buttons: [{ text: 'Review' }],
  },
  [PlModalType.BULK_SERVER_ERROR]: {
    title: 'Server Error',
    message: `<div class="wider-modal-content d-flex flex-column align-items-center">
        <div class="m-4">
          <img src="/assets/img/user-management/bulk-server-error.svg" />
        </div>
        <div>
          We are sorry, we could not process your submission.
          <br/>
          Please try again or <a href="mailto:asksupport@presence.com">contact support</a>.
        </div>
      </div>`,
    buttons: [{ text: 'OK' }],
  },
  [PlModalType.BULK_IMPORT_AS_IS]: {
    title: 'Before you proceed...',
    image: '/assets/img/bulk-upload/import-as-is.png',
    message:
      'We found some data fields with errors, so we will leave them out of the student record.  However, you can always add that information in the student profile after the record is created.',
    buttons: [
      { text: 'Back', type: 'secondary' },
      { text: 'Proceed', value: 'continue' },
    ],
  },
  [PlModalType.INPUT_WRONG_FILE_TYPE]: {
    title: 'We’re having trouble with that file',
    message:
      'This is an unsupported file type,<br/> please download and use the template or upload a csv or xlsx file',
    image: '/assets/img/bulk-upload/questions.png',
    buttons: [{ text: 'OK' }],
  },
  [PlModalType.BULK_EMPTY_DATA]: {
    title: 'Nothing to Import',
    message: `The file you selected was blank, and so there is nothing to import.`,
    image: '/assets/img/bulk-upload/warning.png',
    buttons: [{ text: 'OK' }],
  },
  [PlModalType.DELETE_OBJECTIVE]: {
    title: null,
    message: `    <div class="modal-icon trash-icon">
      <span
        class="material-icons-outlined"
        aria-hidden="false"
        aria-label="trash icon"
        >delete</span
      >
    </div><div class="mt-3"><strong>Are you sure?</strong></div><div class="pt-3">Deleting an objective will remove it from your goal. You will still be able to view previous data tied to these objectives in the sessions tab.
      </div>`,
    buttons: [
      { text: 'No, Keep It', type: 'secondary', class: 'mat-stroked-button' },
      { text: 'Yes, Delete!', value: 'yes', class: 'mat-flat-button' },
    ],
    align: 'center',
    alignButtons: 'center',
  },
  [PlModalType.DELETE_GOAL]: {
    title: null,
    message: `    <div class="modal-icon trash-icon">
      <span
        class="material-icons-outlined"
        aria-hidden="false"
        aria-label="trash icon"
        >delete</span
      >
    </div><div class="mt-3"><strong>Are you sure?</strong></div><div class="pt-3">Deleting a goal will also delete all objectives tied <br/> to the goal. You will still be able to view previous <br/> data tied to these objectives in the sessions tab.
      </div>`,
    buttons: [
      { text: 'No, Keep It', type: 'secondary', class: 'mat-stroked-button' },
      { text: 'Yes, Delete!', value: 'yes', class: 'mat-flat-button' },
    ],
    align: 'center',
    alignButtons: 'center',
  },
  [PlModalType.CONTACT_SUPPORT]: {
    title: 'Contact Support',
    message: `
      <div class="wider-modal-content d-flex flex-column align-items-center">
        <div class="m-4">
          <img src="/assets/img/user-management/contact-support.svg" />
        </div>
        <div>To purchase more licenses please contact client support:</div>
        <div>
          Via email -
          <a href="mailto:${environment.support_email}"
            >${environment.support_email}</a
          >
        </div>
        <div>Via phone - <a href="tel:${environment.support_phone}">(844) 415-4592 opt.1</a></div>
        <div>
          Via Chat - Help & Support chat in the main menu
        </div>
    </div>
    `,
    buttons: [{ text: 'OK' }],
  },
  [PlModalType.EDIT_OBJECTIVE]: {
    title: null,
    message: `    <div class="modal-icon draw">
      <span
        class="material-icons-outlined"
        aria-hidden="false"
        aria-label="draw icon"
        >draw</span
      >
    </div><div class="mt-3"><strong>Are you sure?</strong></div><div class="pt-3 modal-text-container">Any previously entered scores for the old objective will <br/> be changed and tied to the edited objectives. To retain <br/> the old data, we suggest creating a new objective <br/> instead.</div>`,
    buttons: [
      { text: 'Discard Changes', type: 'secondary' },
      { text: 'Save Changes', value: 'yes' },
    ],
    align: 'center',
    alignButtons: 'center',
  },
};

@Injectable()
export class PlModalService {
  supportEmail = supportEmail;

  constructor(private dialog: MatDialog) {}

  coreDeleteConfirmation(
    title: string,
    message: string,
    confirmButton = 'Yes',
    cancelButton = 'Cancel'
  ): Observable<boolean> {
    return this.openDialog({
      title,
      message,
      buttons: [
        { text: cancelButton, type: 'secondary' },
        { text: confirmButton, value: 'yes' },
      ],
      align: 'left',
    }).pipe(
      map((r) => r === 'yes'),
      filter((r) => r)
    );
  }

  openDeleteConfirmation(entityName: string): Observable<boolean> {
    return this.coreDeleteConfirmation(
      'Confirm',
      `Are you sure you want to delete this ${entityName}?<br/> The data will not be recoverable.`
    );
  }

  openDeleteConfirmationV2(message: string): Observable<boolean> {
    return this.openDialog(
      {
        title: null,
        message: `<div class="modal-icon trash-icon">
      <span
        class="material-icons-outlined"
        aria-hidden="false"
        aria-label="trash icon"
        >delete</span
      >
    </div><div class="mt-3"><strong>Are you sure?</strong></div><div class="pt-3"> ${message}
      </div>`,
        buttons: [
          {
            text: 'No, Keep It',
            type: 'secondary',
            class: 'mat-stroked-button',
          },
          { text: 'Yes, Delete!', value: 'yes', class: 'mat-flat-button' },
        ],
        align: 'center',
        alignButtons: 'center',
      },
      0,
      360
    ).pipe(filter((r) => r === 'yes'));
  }

  open(configurationType: PlModalType) {
    const data: PlModalMessageData = configurations[configurationType];
    if (!data) {
      return of();
    }
    return this.openDialog(data);
  }

  openDialog(
    data: PlModalMessageData,
    minWidth = 400,
    maxWidth?: number
  ): Observable<any> {
    const dialog = this.dialog.open(ModalMessageComponent, {
      data,
      minWidth: minWidth ? `${minWidth}px` : undefined,
      maxWidth: maxWidth ? `${maxWidth}px` : undefined,
      hasBackdrop: true,
      disableClose: true,
    });
    return dialog.afterClosed();
  }
}
